import { Http } from '@mygpt/app/http';

export interface JobParams {
  lang: string;
}

// 定义角色接口
interface Role {
  id: number;
  code: string;
  name: string;
  enabled: boolean;
  createdAt: string;
  updatedAt: string;
}

// 定义获取角色API返回的数据格式接口
interface GetAuthorityRolesResponse {
  success: boolean;
  outputParameters: Record<string, any>;
  fieldErrors: any[];
  roles: Role[];
}

// 定义获取角色用户请求参数接口
interface GetAuthorityRoleUsersParams {
  roleCodes: string[];
}

// 定义获取角色用户API返回的数据格式接口
interface GetAuthorityRoleUsersResponse {
  success: boolean;
  outputParameters: Record<string, any>;
  fieldErrors: any[];
  roles: Role[];
  roleIdToUserIds: Record<string, string[]>;
  // 禁用用户的：角色-用户ID 对照表
  roleIdToDisabledUserIds: Record<string, string[]>;
}

// 定义部门接口
interface Department {
  id: string;
  name: string;
  code: string;
  description: string;
  companyId: string;
  tenantId: number;
  managerId: string;
  parentId: number;
  enabled: boolean;
  createAt: string;
  updateAt: string;
}

// 定义 API 返回的数据格式接口
interface GetDepartmentResponse {
  success: boolean;
  outputParameters: Record<string, any>;
  fieldErrors: any[];
  departments: Department[];
}

// 定义用户接口
interface User {
  id: string;
  uuid?: string;
  name: string;
  email: string;
  actived: boolean;
  enabled: boolean;
  defaultCompanyId: string;
  userCompanyActived: boolean;
  userCompanyEnabled: boolean;
  createdAt: string;
  updatedAt: string;
  authorityCodes: string[];
}

// 定义 API 返回的数据格式接口
interface GetDepartmentUsersResponse {
  success: boolean;
  outputParameters: Record<string, any>; // 如果 outputParameters 是一个空对象
  fieldErrors: any[]; // 如果 fieldErrors 是一个空数组
  departmentIdToUserIdsMap: Record<string, string[]>; // 部门ID到用户ID的映射
  isolatedUserIds: string[]; // 没有分配部门的用户ID
  users: User[]; // 用户列表
}

// 定义用户列表API返回的数据格式接口
interface GetUserListResponse {
  success: boolean;
  outputParameters: Record<string, any>;
  fieldErrors: any[];
  departmentIdToUserIdsMap: Record<string, string[]>;
  isolatedUserIds: string[];
  users: User[];
}

const OGS_API_BASE_URL = import.meta.env.VITE_OGS_API_BASE_URL;

/**
 * 获取Admin用户信息
 * @deprecated 应该用 authLogin 那个接口，现在前端登录的入口是felo-mygpt后端，然后后端再决定调用ogs的登录/auth0的登录
 * @param params
 * @returns
 */
export const getUserInfoForAdmin = ({ email, password }: { email: string; password: string }) => {
  const { post } = Http.use();
  return post<any>(`${OGS_API_BASE_URL}/user/login/password`, { email, password });
};

/**
 * 获取当前账户公司
 * @param params
 * @returns
 */
export const getMyCompanies = () => {
  const { get } = Http.use();
  return get<any>(`${OGS_API_BASE_URL}/user/my/companies`);
};

/**
 * 获取指定用户公司
 * @param params
 * @returns
 */
// export const getUserCompanies = (userId: string) => {
//   const { get } = Http.use();
//   return get<any>(`${OGS_API_BASE_URL}/user/${userId}/companies`);
// };

/**
 * 获取职位角色
 * @param params
 * @returns
 */
export const getJobRoles = ({ lang }: JobParams) => {
  const { post } = Http.use();
  return post<any>(`${OGS_API_BASE_URL}/config/job/roles`, { lang });
};

/**
 * 获取职位职务
 * @param params
 * @returns
 */
export const getJobPositions = ({ lang }: JobParams) => {
  const { post } = Http.use();
  return post<any>(`${OGS_API_BASE_URL}/config/job/positions`, { lang });
};
/**
 * 创建部门
 * @param params
 * @returns
 */
export const createDepartment = ({
  name,
  code,
  description,
  parentId,
  companyId,
}: {
  name: string;
  code: string;
  description: string;
  parentId: number;
  companyId: number;
}) => {
  const { post } = Http.use();
  return post<any>(`${OGS_API_BASE_URL}/department/create`, { name, code, description, parentId, companyId });
};

/**
 * 更新部门
 * @param params
 * @returns
 */
export const updateDepartment = ({
  id,
  name,
  code,
  description,
}: {
  id: string;
  name: string;
  code: string;
  description: string;
}) => {
  const { post } = Http.use();
  return post<any>(`${OGS_API_BASE_URL}/department/update`, { id, name, code, description });
};

/**
 * 获取指定公司部门
 * @param params
 * @returns
 */
export const getDepartmentByCompanyId = (companyId: string): Promise<GetDepartmentResponse> => {
  const { get } = Http.use();
  return get<GetDepartmentResponse>(`${OGS_API_BASE_URL}/department/inherited/company/${companyId}`).then(
    (response) => {
      return response.data;
    }
  );
};

/**
 * 获取指定公司部门用户
 * @param params
 * @returns
 */
export const getDepartmentUsersByCompanyId = (companyId: string): Promise<GetDepartmentUsersResponse> => {
  const { get } = Http.use();
  return get<GetDepartmentUsersResponse>(`${OGS_API_BASE_URL}/user/company/${companyId}/department/users`).then(
    (response) => response.data
  );
};

/**
 * 创建用户
 * @param params
 * @returns
 */
export const createUser = ({
  name,
  email,
  password,
  companyId,
  sysRoleId,
  departmentId,
}: {
  name: string;
  email: string;
  password: string;
  companyId: string;
  sysRoleId?: number;
  departmentId?: string;
}) => {
  const { post } = Http.use();
  return post<any>(`${OGS_API_BASE_URL}/user/create`, { name, email, password, companyId, sysRoleId, departmentId });
};

/**
 * 获取用户列表
 * @param params
 * @returns
 */
export const getUserList = ({
  currentPage,
  pageSize,
  companyId,
  inCompanyUserNameInfixLike,
  enabled = null,
  userCompanyEnabled = true,
}: {
  currentPage: number;
  pageSize: number;
  companyId: string;
  inCompanyUserNameInfixLike?: string;
  enabled: boolean | null; // enabled":null, 返回所有用户。默认是true的，只返回有效的用户。
  userCompanyEnabled?: boolean;
}): Promise<GetUserListResponse> => {
  const { post } = Http.use();
  return post<GetUserListResponse>(`${OGS_API_BASE_URL}/user/search/page`, {
    currentPage,
    pageSize,
    companyId,
    inCompanyUserNameInfixLike,
    enabled,
    // userCompanyEnabled,
  }).then((response) => {
    return response.data;
  });
};

/**
 * 获取公司信息
 * @param params
 * @returns
 */
export const getCompanyProfileInfo = (companyId: string) => {
  const { get } = Http.use();
  return get<any>(`${OGS_API_BASE_URL}/company/${companyId}/profile`);
};

/**
 * 获取当前用户权限
 * @param params
 * @returns
 */
export const getUserAuthority = (companyId: string) => {
  const { get } = Http.use();
  return get<any>(`${OGS_API_BASE_URL}/user/company/${companyId}/my/authority/`);
};

/**
 * 获取公司设置
 * @param params
 * @returns
 */
export const getCompanySetting = (companyId: string) => {
  const { get } = Http.use();
  return get<any>(`${OGS_API_BASE_URL}/company/${companyId}/setting`);
};

/**
 * 移除用户公司
 * @param params
 * @returns
 */
export const unsetUserCompany = ({ userId, companyId }: { userId: string; companyId: string }) => {
  const { post } = Http.use();
  return post<any>(`${OGS_API_BASE_URL}/user/company/unset`, { userId, companyId });
};

/**
 * 编辑用户
 * @param params
 * @returns
 */
export const editUser = ({ userId, companyId, userName }: { userId: string; companyId: string; userName: string }) => {
  const { post } = Http.use();
  return post<any>(`${OGS_API_BASE_URL}/user/company`, { userId, companyId, userName });
};

/**
 * 更新公司信息
 * @param params
 * @returns
 */
export const updateCompany = ({
  id,
  name,
  description,
  initialPassword,
}: {
  id: string;
  name: string;
  description: string;
  initialPassword?: string;
}) => {
  const { post } = Http.use();
  // 初始密码为空时不提交该字段，避免后端把空串当作新密码校验
  return post<any>(`${OGS_API_BASE_URL}/company/update`, {
    id,
    name,
    description,
    ...(initialPassword ? { initialPassword } : {}),
  });
};

/**
 * 删除部门
 * @param params
 * @returns
 */
export const deleteDepartment = ({ id }: { id: string }) => {
  const { delete: deleteHttp } = Http.use();
  return deleteHttp<any>(`${OGS_API_BASE_URL}/department/physical/${id}`);
};

/**
 * 设置用户部门
 * @param params
 * @returns
 */
export const setUserDepartment = ({ userIds, departmentId }: { userIds: string[]; departmentId: string }) => {
  const { post } = Http.use();
  return post<any>(`${OGS_API_BASE_URL}/user/users/department/set`, { userIds, departmentId });
};

/**
 * 移除用户部门
 * @param params
 * @returns
 */
export const unsetUserDepartment = ({ userIds, departmentId }: { userIds: string[]; departmentId: string }) => {
  const { post } = Http.use();
  return post<any>(`${OGS_API_BASE_URL}/user/users/department/unset`, { userIds, departmentId });
};

/**
 * 修改用户密码
 * @param params
 * @returns
 */
export const changeUserPassword = ({ oldPassword, newPassword }: { oldPassword: string; newPassword: string }) => {
  const { post } = Http.use();
  return post<any>(`${OGS_API_BASE_URL}/user/password/change`, { oldPassword, newPassword });
};

/**
 * 获取当前用户默认公司
 * @param params
 * @returns
 */
export const getMyCompanyDefault = () => {
  const { get } = Http.use();
  return get<any>(`${OGS_API_BASE_URL}/user/my/company/default`);
};

/**
 * 重置用户密码
 * @param params
 * @returns
 */
export const resetUserPassword = ({ id, password }: { id: string; password: string }) => {
  const { post } = Http.use();
  return post<any>(`${OGS_API_BASE_URL}/user/password/admin/update`, { id, password });
};

/**
 * 管理员为用户添加API密钥
 * @param params
 * @returns
 */
export const adminCreateUserApiKey = ({
  userId,
  apiKey,
  description,
}: {
  userId: string;
  apiKey: string;
  description?: string;
}) => {
  const { post } = Http.use();
  return post<any>(`/admin/users/${userId}/apikeys`, {
    api_key_value: apiKey,
    description: description || '',
  });
};

/**
 * 管理员获取用户的API密钥列表
 * @param userId
 * @returns
 */
export const adminGetUserApiKeys = (userId: string) => {
  const { get } = Http.use();
  return get<any>(`/admin/users/${userId}/apikeys`);
};

/**
 * 管理员删除用户的API密钥
 * @param params
 * @returns
 */
export const adminDeleteUserApiKey = ({ userId, apiKey }: { userId: string; apiKey: string }) => {
  const { delete: del } = Http.use();
  return del<any>(`/admin/users/${userId}/apikeys/${apiKey}`);
};

/**
 * 修改当前用户密码
 * @param params
 * @returns
 */
export const changeCurrentUserPassword = ({
  oldPassword,
  newPassword,
}: {
  oldPassword: string;
  newPassword: string;
}) => {
  const { post } = Http.use();
  return post<any>(`${OGS_API_BASE_URL}/user/password/change`, { oldPassword, newPassword });
};

/**
 * 用户启用
 * @param params
 * @returns
 */
export const setUserCompany = ({
  userId,
  companyId,
  departmentId,
}: {
  userId: string;
  companyId: string;
  departmentId?: string;
}) => {
  const { post } = Http.use();
  return post<any>(`${OGS_API_BASE_URL}/user/company/set`, { userId, companyId, departmentId });
};

/**
 * 获取权限角色
 * @param params
 * @returns
 */
export const getAuthorityRoles = (): Promise<GetAuthorityRolesResponse> => {
  const { post } = Http.use();
  return post<GetAuthorityRolesResponse>(`${OGS_API_BASE_URL}/authority/roles`, {}).then((response) => {
    return response.data;
  });
};

/**
 * 获取可分配的权限角色
 * @param params
 * @returns
 */
export const getAuthorityAssignableRoles = (): Promise<GetAuthorityRolesResponse> => {
  const { post } = Http.use();
  return post<GetAuthorityRolesResponse>(`${OGS_API_BASE_URL}/authority/roles/assignable`, {}).then((response) => {
    return response.data;
  });
};

/**
 * 获取权限角色用户
 * @param params
 * @returns
 */
export const getAuthorityRoleUsers = (params: GetAuthorityRoleUsersParams): Promise<GetAuthorityRoleUsersResponse> => {
  const { post } = Http.use();
  return post<GetAuthorityRoleUsersResponse>(`${OGS_API_BASE_URL}/authority/role/users`, params).then((response) => {
    return response.data;
  });
};

/**
 * 更新用户
 * @param params
 * @returns
 */
export const updateUser = ({
  id,
  name,
  email,
  enabled,
  userCompanyEnabled,
  sysRoleId,
}: {
  id: string;
  name: string;
  email: string;
  enabled: boolean;
  userCompanyEnabled?: boolean;
  sysRoleId?: number;
}) => {
  const { post } = Http.use();
  return post<any>(`${OGS_API_BASE_URL}/user/update`, { id, name, email, enabled, userCompanyEnabled, sysRoleId });
};

export type ResourceDataType = 'BOT' | 'DATASET';

export interface SetResourcePrincipleParams {
  userIds: string[];
  departmentIds: string[];
  companyAuthorized: boolean;
  dataId: string;
  dataType: ResourceDataType;
  authorityCode: string;
}

export const setResourcePrinciple = (params: SetResourcePrincipleParams) => {
  const { post } = Http.use();
  return post<any>(`${OGS_API_BASE_URL}/authority/resource/principle/set`, params);
};

export interface GetResourcesPrincipleParams {
  dataIds: string[];
  dataType: ResourceDataType;
  authorityCode: string;
}

export interface GetResourcePrincipleParams {
  dataId: string;
  dataType: ResourceDataType;
  authorityCode: string;
}

export const getResourcesPrinciple = (params: GetResourcesPrincipleParams) => {
  const { post } = Http.use();
  return post<any>(`${OGS_API_BASE_URL}/authority/resources/principle/get`, params);
};

export const getResourcePrinciple = (params: GetResourcePrincipleParams) => {
  const { post } = Http.use();
  return post<any>(`${OGS_API_BASE_URL}/authority/resource/principle/get`, params);
};

export interface ResourcePrincipleTypeResponse {
  success: boolean;
  user: boolean;
  department: boolean;
  company: boolean;
}

/**
 * 预查询资源权限类型（无需登录）
 * 返回资源是否设置了用户/部门/公司范围的访问控制
 */
export const getResourcePrincipleType = (params: GetResourcePrincipleParams) => {
  const { post } = Http.use();
  return post<ResourcePrincipleTypeResponse>(`${OGS_API_BASE_URL}/authority/resource/principle/type`, params);
};

/**
 * 同时更新用户部门和角色
 * @param params
 * @returns
 */
export const updateUserDepartmentAndRole = ({
  userIds,
  departmentId,
  sysRoleId,
}: {
  userIds: string[];
  departmentId: string;
  sysRoleId: string;
}) => {
  const { post } = Http.use();
  return post<any>(`${OGS_API_BASE_URL}/user/update/departmentAndRole/batch`, { userIds, departmentId, sysRoleId });
};

/**
 * 批量删除用户（本地部署版）
 * @param params 包含用户ID数组的参数
 * @returns
 */
export const batchDeleteUsers = ({ ids }: { ids: string[] }) => {
  const { post } = Http.use();
  return post<any>(`${OGS_API_BASE_URL}/user/physical/batch/delete`, { ids });
};

/**
 * 将多个用户从公司中移除（Cloud版）
 * @param params 包含用户ID数组和公司ID的参数
 * @returns
 */
export const unsetUsersCompany = ({
  userIds,
  companyId,
}: {
  userIds: (string | number)[];
  companyId: string | number;
}) => {
  const { post } = Http.use();
  return post<any>(`${OGS_API_BASE_URL}/user/users/company/unset`, { userIds, companyId });
};

/**
 * 批量删除用户与公司的关联（Cloud版）
 * @param params 包含用户ID数组
 */
export const deleteUsersCompany = ({ userIds }: { userIds: (string | number)[] }) => {
  const { post } = Http.use();
  return post<any>(`${OGS_API_BASE_URL}/user/users/company/delete`, { userIds });
};

// ===================== 邀请相关API（Cloud版） =====================

/**
 * 邀请参数接口
 */
export interface InvitationParams {
  user_id: string; // 被邀请人的 user_id
  user_ogs_id: string; // 被邀请人的 OGS ID
  email: string; // 被邀请人邮箱
  inviter_id: string; // 邀请人的 user_id
  inviter_ogs_id: string; // 邀请人的 OGS ID
  inviter_name: string; // 邀请人名称
  company_id: string; // 公司 ID
  department_id?: string; // 部门 ID（可选）
  sys_role_id?: string; // 角色 ID（可选）
  metadata?: Record<string, any>; // 元数据（可选）
}

/**
 * 邀请状态
 */
export type InviteStatus = 'pending' | 'accepted' | 'rejected' | 'expired';

/**
 * 邀请记录接口
 */
export interface OrganizationInvite {
  id: string;
  invitee_user_id: string;
  invitee_ogs_id: string;
  invitee_email: string;
  inviter_id: string;
  inviter_ogs_id: string;
  inviter_name: string;
  company_id: string;
  department_id: string | null;
  sys_role_id: string | null;
  status: InviteStatus;
  created_at: string;
  updated_at: string;
  accepted_at: string | null;
  metadata: any;
}

/**
 * 邀请通知包装结构
 */
export interface InvitationNotification {
  type: string;
  invitation: OrganizationInvite;
}

/**
 * 发送组织邀请（支持单个/批量）
 * @param params 单个或多个邀请参数
 * @returns
 */
export const sendInvitation = (params: InvitationParams | InvitationParams[]) => {
  const { post } = Http.use();
  const payload = Array.isArray(params) ? params : [params];
  return post<any>('/invitations', payload);
};

/**
 * 获取收到的邀请通知列表
 * @returns 邀请列表
 */
export const getReceivedInvitations = () => {
  const { get } = Http.use();
  return get<InvitationNotification[]>('/invitations/notifications');
};

/**
 * 接受邀请
 * @param id 邀请ID
 * @returns
 */
export const acceptInvitation = (id: string) => {
  const { post } = Http.use();
  return post<any>(`/invitations/${id}/accept`);
};

/**
 * 拒绝邀请
 * @param id 邀请ID
 * @returns
 */
export const rejectInvitation = (id: string) => {
  const { post } = Http.use();
  return post<any>(`/invitations/${id}/reject`);
};

/**
 * 通过邮箱搜索用户（检查是否可邀请）
 * @param email 邮箱地址
 * @returns 用户信息
 */
export const searchUserByEmail = (email: string) => {
  const { get } = Http.use();
  return get<any>('/users/search', { params: { email } });
};

/**
 * 根据邮箱中缀模糊查询用户（OGS 接口）
 * @param email 邮箱关键词（至少3个字符）
 * @param recordCount 返回记录数上限
 * @returns 匹配的用户列表
 */
export const searchUserByEmailInfix = (email: string, recordCount: number = 10) => {
  const { post } = Http.use();
  return post<any>(`${OGS_API_BASE_URL}/user/email/infix`, { email, recordCount });
};

/**
 * 创建组织（Cloud版启用组织管理）
 * @param params 组织信息
 * @returns
 */
export const createOrganization = (params: { companyName: string }) => {
  const { post } = Http.use();
  return post<any>(`${OGS_API_BASE_URL}/user/my/company/create`, params);
};

/**
 * 重新生成企业Token
 * 创建企业后调用，更新当前用户的token以包含企业信息
 * @param companyId 企业ID
 * @returns 新的token信息
 */
export const regenerateCompanyToken = (companyId: string) => {
  const { post } = Http.use();
  return post<any>(`/regenerate-company-token`, { company_id: companyId });
};

/**
 * 退出组织（需要公司没有其他成员才能退出）
 * @param params 包含公司ID的参数
 * @returns
 */
export const quitOrganization = (params: { companyId: number }) => {
  const { post } = Http.use();
  return post<any>(`${OGS_API_BASE_URL}/user/my/company/quit`, params);
};

/**
 * 处理组织资源（删除或转移到个人）
 * @param params 资源处理参数
 * @returns
 */
export const handleOrganizationResources = (params: {
  action: 'delete' | 'transfer';
  company_id: string;
  user_id: string;
}) => {
  const { post } = Http.use();
  return post<{ robots_affected: number; datasets_affected: number }>('/users/organization-resources/handle', params);
};
